import classNames from 'classnames'
import { cloneElement, useRef } from 'react'
import { IconButtonSize, IconButtonVariants } from '../button'
import { LoadingIndicator, LoadingIndicatorVariant } from '../loading-indicator'
import styles from './Counter.module.scss'
import { CounterSize, CounterVariant } from './types'
import type { CounterProps } from './types'

const Counter = ({
  leadingIconButton,
  trailingIconButton,
  quantity,
  variant = CounterVariant.neutral,
  disabled = false,
  size = CounterSize.small,
  loading,
  className,
}: CounterProps) => {
  const content = useRef(null)

  const getTextClass = () => {
    switch (size) {
      case CounterSize.small:
        return 'pintxo-typography-callout3'
      case CounterSize.medium:
        return 'pintxo-typography-callout2'
      case CounterSize.large:
        return 'pintxo-typography-callout1'
      default:
        return 'pintxo-typography-callout2'
    }
  }
  const getIconButtonSize = () => {
    switch (size) {
      case CounterSize.small:
        return IconButtonSize.xsmall
      case CounterSize.medium:
        return IconButtonSize.small
      case CounterSize.large:
        return IconButtonSize.large
      default:
        return null
    }
  }
  const LeadingIconComponent = () => {
    return (
      leadingIconButton &&
      cloneElement(leadingIconButton, {
        disabled: leadingIconButton.props.disabled || loading || disabled,
        variant: IconButtonVariants.transparent,
        size: getIconButtonSize(),
        loading: leadingIconButton.props.loading || (!quantity && loading),
      })
    )
  }
  const TrailingIconComponent = () => {
    return (
      trailingIconButton &&
      cloneElement(trailingIconButton, {
        disabled: trailingIconButton.props.disabled || loading || disabled,
        variant: IconButtonVariants.transparent,
        size: getIconButtonSize(),
        loading: trailingIconButton.props.loading || (!quantity && loading),
      })
    )
  }

  const getLoadingVariant = () => {
    switch (variant) {
      case CounterVariant.floating:
        return LoadingIndicatorVariant.primary
      case CounterVariant.neutral:
        return LoadingIndicatorVariant.persistentLight
      default:
        return LoadingIndicatorVariant.primary
    }
  }

  return (
    <>
      <div
        className={classNames(styles['pintxo-counter'], className)}
        data-variant={variant}
        data-size={size}
        data-disabled={disabled}
        data-loading={loading}
      >
        <div ref={content} className={styles['pintxo-counter__expandable-container']}>
          <LeadingIconComponent />
          {quantity && (
            <div className={styles['pintxo-counter__container']}>
              <div
                className={classNames({
                  [styles['pintxo-counter__container__content']]: true,
                  [styles['pintxo-counter__container__content--loading']]: loading,
                  [styles['pintxo-counter__container__content--quantity']]: !loading,
                })}
              >
                <span
                  className={classNames(
                    styles['pintxo-counter__container__content__quantity'],
                    getTextClass()
                  )}
                >
                  {quantity}
                </span>
                <span className={styles['pintxo-counter__container__content__loading']}>
                  {loading && <LoadingIndicator size={size} variant={getLoadingVariant()} />}
                </span>
              </div>
            </div>
          )}
          <TrailingIconComponent />
        </div>
      </div>
    </>
  )
}

export default Counter
